import { Button, Flex, Skeleton, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { relativeTime } from "./helpers/relative-time.ts"
import { useRecentActivityInView } from "./hooks/useRecentActivityInView.ts"
import { EntityIcon } from "./map/EntityIcon.tsx"
import type {
  AnalyticsRecentActivityEvent,
  EntityType
} from "./redux/global-map.types"

const { Title, Text } = Typography

interface RecentActivityInViewProps {
  onSelectEvent: (event: AnalyticsRecentActivityEvent) => void
  onViewAll: () => void
}

interface EntityColumnProps {
  entityType: EntityType
  heading: string
  emptyLabel: string
  events: AnalyticsRecentActivityEvent[]
  onSelectEvent: (event: AnalyticsRecentActivityEvent) => void
}

function EntityColumn({
  entityType,
  heading,
  emptyLabel,
  events,
  onSelectEvent
}: EntityColumnProps) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2 sm:px-4 first:sm:pl-0 last:sm:pr-0">
      <div className="flex items-center gap-1.5">
        <EntityIcon entityType={entityType} className="size-4" />
        <Text className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          {heading}
        </Text>
      </div>
      {events.length === 0 ? (
        <div className="flex min-h-[64px] items-center justify-center rounded-md border border-dashed border-[#E3E8EF] px-2 py-3">
          <Text type="secondary" className="text-xs">
            {emptyLabel}
          </Text>
        </div>
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row">
          {events.map((event, index) => (
            <button
              key={`${event.recordId}-${event.timestamp}-${index}`}
              type="button"
              onClick={() => onSelectEvent(event)}
              className="flex min-w-0 flex-1 items-start gap-2 rounded-md border border-[#E3E8EF] bg-white p-2.5 text-left hover:border-blue-300 hover:bg-blue-50/40"
            >
              <EntityIcon
                entityType={entityType}
                className="mt-0.5 size-8 shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[11px] text-slate-400">
                  {event.description}
                </div>
                <div className="truncate text-sm font-semibold text-gray-900">
                  {event.recordTitle}
                </div>
                <div className="truncate text-[11px] text-slate-400">
                  {relativeTime(event.timestamp)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * "Recent Activity in View" — beneath the map column. Reuses the same
 * aggregate computation the Analytics tab's own Recent Activity card is
 * built on (`compute_recent_activity_by_entity`, a light dedicated endpoint
 * rather than the full `compute_analytics()` — see that endpoint's own
 * docstring for why: the Map tab is this app's highest-traffic surface),
 * scoped to the SAME shared filters/search/layers every other Map-tab
 * surface reads.
 *
 * Per explicit product direction, this card shows ONLY Leads and Projects
 * (Workforce/Subcontractor/At-Risk-SLA have no backing data model here, the
 * same reasoning the KPI row and map legend already apply), split into two
 * equal-width halves rather than the reference mockup's four equal columns
 * — each half independently guaranteed up to its own latest 2 events, so
 * one entity type being more active can never crowd the other out (see the
 * backend's own `_recent_activity_events_for_entity` docstring).
 */
const RecentActivityInView = ({
  onSelectEvent,
  onViewAll
}: RecentActivityInViewProps) => {
  const { t } = useTranslation()
  const { data, status, error, refetch } = useRecentActivityInView()

  const isLoading = status === "loading"
  const isError = status === "error"
  const isEmpty =
    status === "idle" &&
    data !== null &&
    data.lead.length === 0 &&
    data.project.length === 0

  return (
    <div
      className="shrink-0 rounded-lg border border-[#E3E8EF] bg-white p-4"
      data-testid="global-map-recent-activity"
    >
      <Flex justify="space-between" align="center" className="mb-3">
        <Title level={5} className="!mb-0 text-sm font-semibold">
          {t("globalMap.recentActivity.title")}
        </Title>
        <button
          type="button"
          onClick={onViewAll}
          className="cursor-pointer border-none bg-transparent p-0 text-xs font-medium text-blue-600 hover:underline"
        >
          {t("globalMap.recentActivity.viewAll")}
        </button>
      </Flex>

      {isLoading && <Skeleton active paragraph={{ rows: 2 }} title={false} />}

      {isError && (
        <div className="flex flex-col items-center justify-center gap-1 rounded-md border border-dashed border-[#E3E8EF] py-6">
          <Text type="secondary" className="text-xs">
            {t("globalMap.recentActivity.loadError")}
          </Text>
          {error?.code && (
            <Text type="secondary" className="text-[11px]">
              {error.code}
            </Text>
          )}
          <Button size="small" onClick={refetch} className="mt-1">
            {t("globalMap.recentActivity.retry")}
          </Button>
        </div>
      )}

      {isEmpty && (
        <div className="flex items-center justify-center rounded-md border border-dashed border-[#E3E8EF] py-6">
          <Text type="secondary" className="text-xs">
            {t("globalMap.recentActivity.empty")}
          </Text>
        </div>
      )}

      {!isLoading && !isError && !isEmpty && data && (
        <div className="flex flex-col gap-4 sm:flex-row sm:divide-x sm:divide-[#E3E8EF]">
          <EntityColumn
            entityType="lead"
            heading={t("globalMap.entityLabel.lead")}
            emptyLabel={t("globalMap.recentActivity.emptyLead")}
            events={data.lead}
            onSelectEvent={onSelectEvent}
          />
          <EntityColumn
            entityType="project"
            heading={t("globalMap.entityLabel.project")}
            emptyLabel={t("globalMap.recentActivity.emptyProject")}
            events={data.project}
            onSelectEvent={onSelectEvent}
          />
        </div>
      )}
    </div>
  )
}

export default RecentActivityInView
